import { useDownloadCenter, DownloadStatus, type DownloadEntry } from '../DownloadCenterContext'
import { formatTimestampWithZone } from 'utils/format-date'

function FileIcon({ status }: { status: DownloadStatus }) {
    const color = status === DownloadStatus.FAILED ? '#ef4444' : '#1e3a5f'
    return (
        <svg width="28" height="34" viewBox="0 0 28 34" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0">
            <path
                d="M17 1H4C2.34315 1 1 2.34315 1 4V30C1 31.6569 2.34315 33 4 33H24C25.6569 33 27 31.6569 27 30V11L17 1Z"
                fill={color}
                stroke={color}
                strokeWidth="1"
            />
            <path d="M17 1V11H27" fill="white" fillOpacity="0.25" />
            <path d="M17 1V11H27" stroke={color} strokeWidth="1" />
        </svg>
    )
}

function FolderIcon({ status }: { status: DownloadStatus }) {
    const color = status === DownloadStatus.FAILED ? '#ef4444' : '#d97706'
    return (
        <svg width="34" height="28" viewBox="0 0 34 28" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0">
            <path
                d="M2 4C2 2.34315 3.34315 1 5 1H13L16 5H29C30.6569 5 32 6.34315 32 8V24C32 25.6569 30.6569 27 29 27H5C3.34315 27 2 25.6569 2 24V4Z"
                fill={color}
                stroke={color}
                strokeWidth="1"
            />
            <path d="M2 10H32" stroke="white" strokeOpacity="0.3" strokeWidth="1" />
        </svg>
    )
}

function StatusBadge({ status }: { status: DownloadStatus }) {
    if (status === DownloadStatus.DOWNLOADING) {
        return (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-blue-300 bg-blue-50 px-3 py-1 text-[11px] font-semibold text-blue-700 dark:border-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3V4a10 10 0 00-10 10h2z" />
                </svg>
                Generating…
            </span>
        )
    }
    if (status === DownloadStatus.FAILED) {
        return (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-red-300 bg-red-50 px-3 py-1 text-[11px] font-semibold text-red-600 dark:border-red-700 dark:bg-red-900/30 dark:text-red-400">
                <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
                Failed
            </span>
        )
    }
    return (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-green-300 bg-green-50 px-3 py-1 text-[11px] font-semibold text-green-700 dark:border-green-700 dark:bg-green-900/30 dark:text-green-400">
            <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14l-4-4 1.41-1.41L10 13.17l6.59-6.59L18 8l-8 8z" />
            </svg>
            Completed
        </span>
    )
}

function DownloadRow({ entry, onDownload }: { entry: DownloadEntry; onDownload: (id: string) => Promise<void> }) {
    const canDownload = entry.status === DownloadStatus.COMPLETED && (!!entry.blobUrl || !!entry.url || (!!entry.fileUrls && entry.fileUrls.length > 0))
    const isFolder = entry.entryType === 'folder'

    const handleClick = () => {
        if (canDownload) onDownload(entry.id)
    }

    return (
        <li
            onClick={handleClick}
            className={[
                'flex items-center gap-4 px-6 py-4',
                canDownload ? 'cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-slate-700/50' : '',
            ].join(' ')}
        >
            {isFolder ? <FolderIcon status={entry.status} /> : <FileIcon status={entry.status} />}
            <div className="min-w-0 flex-1">
                <p
                    className={`truncate text-sm font-semibold ${entry.status === DownloadStatus.FAILED ? 'text-red-500 dark:text-red-400' : 'text-slate-900 dark:text-slate-100'}`}
                >
                    {entry.filename}
                </p>
                {entry.status === DownloadStatus.FAILED && entry.error ? (
                    <p className="mt-0.5 truncate text-xs text-red-500 dark:text-red-400">{entry.error}</p>
                ) : entry.completedAt ? (
                    <>
                        <p className="mt-0.5 text-xs text-slate-400 dark:text-slate-500">Completed on {formatTimestampWithZone(entry.completedAt)}</p>
                    </>
                ) : (
                    <p className="mt-0.5 text-xs text-blue-500 dark:text-blue-400">Generating…</p>
                )}
            </div>
            <StatusBadge status={entry.status} />
        </li>
    )
}

export function DownloadCenterModal() {
    const { downloads, isOpen, setIsOpen, triggerDownload, clearAll } = useDownloadCenter()

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4" onClick={() => setIsOpen(false)}>
            <div className="relative w-full max-w-2xl rounded-2xl bg-white shadow-2xl dark:bg-slate-900" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-5">
                    <div className="flex items-center gap-3">
                        <svg className="h-6 w-6 text-slate-800 dark:text-slate-200" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
                            />
                        </svg>
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Downloads</h2>
                    </div>
                    <div className="flex items-center gap-2">
                        {downloads.length > 0 && (
                            <button
                                type="button"
                                onClick={clearAll}
                                title="Clear all"
                                className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                            >
                                Clear all
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => setIsOpen(false)}
                            title="Close"
                            className="text-slate-400 transition hover:text-slate-700 dark:text-slate-500 dark:hover:text-slate-200"
                        >
                            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Divider */}
                <div className="mx-6 border-t border-slate-100 dark:border-slate-800" />

                {/* List */}
                <div className="max-h-[480px] overflow-y-auto">
                    {downloads.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-slate-400 dark:text-slate-500">
                            <svg className="mb-3 h-12 w-12 opacity-40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
                                />
                            </svg>
                            <p className="text-sm font-medium">No downloads yet</p>
                            <p className="mt-1 text-xs">Files you download will appear here</p>
                        </div>
                    ) : (
                        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                            {downloads.map((entry) => (
                                <DownloadRow key={entry.id} entry={entry} onDownload={triggerDownload} />
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    )
}
